import * as React from 'react';
import { useMutation } from 'react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { TripStopType, TripStopLocation, TripDirection } from 'contracts/transport-routes';
import { useTranslation } from 'hooks';
import { useFetchCityByFieldAndValue } from 'hooks/api/cities';
import { useCityOptions, useUfOptions } from 'hooks/api/options';
import { Card, CardContent } from 'components/ui/card';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs, type SelectContentProps } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { TripStopTypeSelector } from './trip-stop-type-selector';
import { TripStopsList, type TripStopsListRef } from './trip-stops-list';
import { formSchema, type FormSchema } from './form-schema';
import { translations } from './translations';

const DEFAULT_STOP_TYPE: TripStopType = 'city';

type Ref = {
  getStopLocations: () => TripStopLocation[];
  setStopLocations: (stops: TripStopLocation[]) => void;
}

export type TripStopsManagerRef = Ref;

type Props = {
  tripDirection: TripDirection
};

export const TripStopsManager = React.forwardRef<Ref, Props>(({ tripDirection }, ref) => {
  const { getTranslation: t } = useTranslation(translations);

  const ufOptions = useUfOptions();
  const { fetchCities } = useCityOptions();
  const { fetchCityByFieldAndValue } = useFetchCityByFieldAndValue();

  const [tripStopType, setTripStopType] = React.useState<TripStopType>(DEFAULT_STOP_TYPE);
  const [cityOptions, setCityOptions] = React.useState<SelectContentProps[]>([]);

  const tripStopsListRef = React.useRef<TripStopsListRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const cityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setCityOptions(data.singleSelectOptions);
    },
  });

  const cityMutation = useMutation({
    mutationFn: fetchCityByFieldAndValue,
  });

  const onUfChange = React.useCallback((uf: string) => {
    form.resetField('ibge');
    cityOptionsMutation.mutate(uf);
  }, [form, cityOptionsMutation]);

  const onSubmitForm = React.useCallback(async (formData: FormSchema) => {
    if (!tripStopsListRef.current) return;

    const generatedId = ((): string => {
      if (formData.type === 'coordinates') {
        return `${formData.latitude},${formData.longitude}`;
      } else if (formData.type === 'city') {
        return `${formData.ibge}`;
      }
      return '';
    })();

    if (formData.type === 'coordinates') {
      tripStopsListRef.current.addStopLocation({
        id: generatedId,
        type: formData.type,
        lat: formData.latitude!,
        lng: formData.longitude!,
      });
      form.setValue('latitude', '');
      form.setValue('longitude', '');
    } else if (formData.type === 'city') {
      cityMutation.mutate({ field: 'ibge', value: formData.ibge! }, {
        onSuccess: city => {
          tripStopsListRef.current?.addStopLocation({
            id: generatedId,
            type: formData.type,
            ibge: formData.ibge!,
            lat: city?.latitude!,
            lng: city?.longitude!,
            uf: city?.state!,
            city_name: city?.name!,
          });
          form.setValue('uf', '');
          form.setValue('ibge', '');
        },
      });
    }
  }, [form]);

  const onReceiveTripStopLocations = React.useCallback((stops: TripStopLocation[]) => {
    tripStopsListRef.current?.setStopLocations(stops);
  }, []);

  React.useEffect(() => {
    form.setValue('type', tripStopType);
  }, [tripStopType]);

  React.useImperativeHandle(ref, () => {
    return {
      getStopLocations: () => tripStopsListRef.current?.getStopLocations() || [],
      setStopLocations: onReceiveTripStopLocations,
    };
  }, [onReceiveTripStopLocations]);

  return (
    <div>
      <div className={'flex flex-row gap-4 items-center py-4'}>
        <h2 className={'font-GilroyBold text-slate-600 text-[18px]'}>{t('title')}</h2>
        <span className={'font-sans text-slate-500 text-sm'}>{t('subtitle')}</span>
      </div>
      <Card>
        <CardContent className={'pt-6'}>
          <TripStopTypeSelector
            defaultValue={DEFAULT_STOP_TYPE}
            onChange={setTripStopType}
          />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitForm)} className={'flex flex-row gap-4 mt-6'}>
              {tripStopType === 'city' && (
                <div className={'grid grid-cols-2 gap-4 w-full'}>
                  <FormField
                    control={form.control}
                    name={'uf'}
                    render={({ field }) => (
                      <ComboboxGs
                        label={t('stopUf')}
                        form={form}
                        fieldName={field.name}
                        fieldValue={field.value}
                        content={ufOptions.singleSelectOptions}
                        onChange={onUfChange}
                      />
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={'ibge'}
                    render={({ field }) => (
                      <ComboboxGs
                        label={t('stopIbge')}
                        form={form}
                        fieldName={field.name}
                        fieldValue={field.value}
                        content={cityOptions}
                      />
                    )}
                  />
                </div>
              )}
              {tripStopType === 'coordinates' && (
                <div className={'grid grid-cols-2 gap-4 w-full'}>
                  <FormField
                    name={'latitude'}
                    control={form.control}
                    render={({ field }) => (
                      <InputGs {...field} label={t('stopLatitude')} />
                    )}
                  />
                  <FormField
                    name={'longitude'}
                    control={form.control}
                    render={({ field }) => (
                      <InputGs {...field} label={t('stopLongitude')} />
                    )}
                  />
                </div>
              )}
              <Button.Root
                type={'submit'}
                size={'sm'}
                className={'mt-[26px]'}
                disabled={cityMutation.isLoading}
              >
                {t('submitButton')}
              </Button.Root>
            </form>
          </Form>
        </CardContent>
      </Card>
      <TripStopsList
        ref={tripStopsListRef}
        tripDirection={tripDirection}
        className={'mt-6'}
      />
    </div>
  );
});
TripStopsManager.displayName = 'TripStopsManager';